import { useMemo, useState } from 'react';
import { Check, IndianRupee, Ruler } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useContent } from '@/lib/content';
import { buildWaLink } from '@/config/brand';
import { trackEvent } from '@/lib/analytics';

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

export const CalculatorCard = () => {
  const { calculator, brand } = useContent();
  const cfg = { price_2d: 550, price_3d: 1100, min_2d: 8, min_3d: 16, tiers: [], ...(calculator || {}) };
  const [decorType, setDecorType] = useState('2d');
  const [feet, setFeet] = useState(8);
  const [above32, setAbove32] = useState(false);

  const min = decorType === '2d' ? cfg.min_2d : cfg.min_3d;
  const rate = decorType === '2d' ? cfg.price_2d : cfg.price_3d;
  const valid = !above32 && feet >= min;
  const total = valid ? feet * rate : 0;

  const tier = useMemo(() => {
    const tiers = (cfg.tiers || []).filter((t) => t.decor_type === decorType);
    const match = tiers
      .filter((t) => feet >= Number(t.min_ft) && (t.max_ft === null || t.max_ft === undefined || t.max_ft === '' || feet <= Number(t.max_ft)))
      .sort((a, b) => Number(b.min_ft) - Number(a.min_ft))[0];
    return match || null;
  }, [cfg.tiers, decorType, feet]);

  const waMsg = `Hi Events by AJ, I used the Birthday Decor Calculator: ${decorType.toUpperCase()} decor, stage length ${feet} ft, approx ${inr(total)}. Please share a detailed quote.`;

  return (
    <div data-testid="calculator-card" className="border border-white/10 bg-surface p-6 sm:p-10">
      <p className="caption-label mb-4">Step 1 — Decor Type</p>
      <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
        {['2d', '3d'].map((t) => (
          <button
            key={t}
            data-testid={`calc-type-${t}`}
            onClick={() => { setDecorType(t); trackEvent('calculator_type', { type: t }); }}
            className={`py-4 text-xs uppercase tracking-[0.25em] transition-colors duration-300 ${
              decorType === t ? 'bg-champagne text-ink font-semibold' : 'bg-ink text-white/60 hover:text-cream'
            }`}
          >
            {t} Decor
          </button>
        ))}
      </div>

      <p className="caption-label mt-8 mb-4">Step 2 — Stage Length (feet)</p>
      <div className="flex items-center gap-4">
        <Ruler size={18} className="text-champagne shrink-0" />
        <select
          data-testid="calc-feet-select"
          value={above32 ? 'above' : feet}
          onChange={(e) => {
            if (e.target.value === 'above') {
              setAbove32(true);
              trackEvent('calculator_above32', { decorType });
            } else {
              setAbove32(false);
              setFeet(Number(e.target.value));
            }
          }}
          className="flex-1 bg-ink border border-white/15 px-4 py-3.5 text-cream focus:border-champagne focus:outline-none cursor-pointer"
        >
          {[8, 12, 16, 20, 24, 28, 32].map((f) => (
            <option key={f} value={f} className="bg-ink">{f} ft stage</option>
          ))}
          <option value="above" className="bg-ink">Larger than 32 ft</option>
        </select>
      </div>

      {above32 && (
        <div data-testid="calc-above32" className="mt-8 border border-champagne/40 bg-ink p-6 sm:p-8 text-center">
          <p className="font-serif text-xl sm:text-2xl font-light text-cream">
            For stages larger than 32 feet, please contact us directly
          </p>
          <a
            data-testid="calc-above32-whatsapp"
            href={buildWaLink(brand.whatsapp, 'Hi Events by AJ, I need birthday decor for a stage larger than 32 feet. Please contact me with details.')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('calculator_enquire', { decorType, feet: '32+' })}
            className="btn-gold mt-6 inline-flex"
          >
            <FaWhatsapp size={16} /> WhatsApp Us
          </a>
        </div>
      )}

      {!above32 && (<>
      {!valid && (
        <p data-testid="calc-min-error" className="mt-4 text-sm text-red-400">
          Minimum stage size for {decorType.toUpperCase()} decor is {min} ft.
        </p>
      )}

      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="caption-label mb-2">Estimated Total</p>
          <p data-testid="calc-total" className="font-serif text-5xl sm:text-6xl font-light text-champagne">
            {valid ? inr(total) : '—'}
          </p>
          {valid && <p className="mt-2 text-white/40 text-xs tracking-wide">{feet} ft × {inr(rate)} per running ft · {decorType.toUpperCase()} decor</p>}
        </div>
      </div>

      {valid && tier && (tier.inclusions || []).length > 0 && (
        <div className="mt-8" data-testid="calc-inclusions">
          <p className="caption-label mb-4">What&rsquo;s Included{tier.min_ft ? ` (${tier.min_ft}${tier.max_ft ? `–${tier.max_ft}` : '+'} ft)` : ''}</p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {tier.inclusions.map((inc) => (
              <li key={inc} className="flex items-start gap-3 text-white/70 text-sm font-light">
                <Check size={15} className="text-champagne mt-0.5 shrink-0" /> {inc}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <a
          data-testid="calc-whatsapp-btn"
          href={valid ? buildWaLink(brand.whatsapp, waMsg) : undefined}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => valid && trackEvent('calculator_enquire', { decorType, feet, total })}
          className={`btn-gold flex-1 ${!valid ? 'opacity-40 pointer-events-none' : ''}`}
        >
          <FaWhatsapp size={16} /> Enquire on WhatsApp
        </a>
        <a data-testid="calc-quote-btn" href="/#enquiry" className={`btn-outline flex-1 ${!valid ? 'opacity-40 pointer-events-none' : ''}`}>
          <IndianRupee size={15} /> Get Your Free Quote
        </a>
      </div>
      </>)}
      <p className="mt-5 text-center text-[11px] text-white/30 tracking-wide">
        Estimate only — final quote depends on theme, flowers and venue. Minimum {cfg.min_2d} ft (2D) / {cfg.min_3d} ft (3D).
      </p>
    </div>
  );
};
